/**
 * Task #4086 — Shared HTTP contracts for the Brand W3 API.
 *
 * These contracts deliberately carry no tenant/product authority supplied by a
 * caller. Store results expose the server-resolved product, fixed to w3suite.
 */
import { z } from 'zod';

const uuidSchema = z.string().uuid();
const timestampSchema = z.string().datetime({ offset: true });
const codeSchema = z.string().trim().min(1).max(128).regex(/^[a-z0-9_.]+$/);
const nameSchema = z.string().trim().min(1).max(128);
const emailSchema = z.string().trim().email().max(255).transform((value) => value.toLowerCase());

export const BrandW3ProductSchema = z.literal('w3suite');
export type BrandW3Product = z.infer<typeof BrandW3ProductSchema>;

export const BrandW3AuthProviderSchema = z.enum(['local', 'oam', 'entra']);
export type BrandW3AuthProvider = z.infer<typeof BrandW3AuthProviderSchema>;

export const BrandW3IdentitySchema = z.object({
  id: uuidSchema,
  user_id: uuidSchema,
  provider: BrandW3AuthProviderSchema,
  issuer: z.string().url().max(2048).nullable(),
  subject: z.string().min(1).max(512).nullable(),
  email: emailSchema,
  created_at: timestampSchema,
  last_authenticated_at: timestampSchema.nullable(),
}).strict();
export type BrandW3Identity = z.infer<typeof BrandW3IdentitySchema>;

export const BrandW3PermissionSchema = z.object({
  id: uuidSchema,
  code: codeSchema,
  name: nameSchema.max(160),
  description: z.string().max(1000).nullable(),
  is_active: z.boolean(),
}).strict();
export type BrandW3Permission = z.infer<typeof BrandW3PermissionSchema>;

export const BrandW3SectorSchema = z.object({
  id: uuidSchema,
  code: codeSchema.max(64),
  name: nameSchema,
  is_active: z.boolean(),
}).strict();
export type BrandW3Sector = z.infer<typeof BrandW3SectorSchema>;

export const BrandW3RoleSchema = z.object({
  id: uuidSchema,
  code: codeSchema.max(64),
  name: nameSchema,
  description: z.string().max(1000).nullable(),
  is_system: z.boolean(),
  is_active: z.boolean(),
  sector: BrandW3SectorSchema,
  permissions: z.array(BrandW3PermissionSchema),
}).strict();
export type BrandW3Role = z.infer<typeof BrandW3RoleSchema>;

export const BrandW3AreaScopeSchema = z.discriminatedUnion('access', [
  z.object({
    area_id: uuidSchema,
    access: z.literal('complete_area'),
    store_ids: z.array(uuidSchema).max(0).default([]),
  }).strict(),
  z.object({
    area_id: uuidSchema,
    access: z.literal('explicit_stores'),
    store_ids: z.array(uuidSchema).min(1).max(10000),
  }).strict(),
]);
export type BrandW3AreaScope = z.infer<typeof BrandW3AreaScopeSchema>;

export const BrandW3ScopeSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('all_areas'),
  }).strict(),
  z.object({
    mode: z.literal('assigned_areas'),
    areas: z.array(BrandW3AreaScopeSchema).max(1000).default([]),
  }).strict(),
]).superRefine((scope, context) => {
  if (scope.mode !== 'assigned_areas') return;
  const areaIds = new Set<string>();
  const storeIds = new Set<string>();
  scope.areas.forEach((area, areaIndex) => {
    if (areaIds.has(area.area_id)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['areas', areaIndex, 'area_id'],
        message: 'Area duplicata nello scope',
      });
    }
    areaIds.add(area.area_id);
    area.store_ids.forEach((storeId, storeIndex) => {
      if (storeIds.has(storeId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['areas', areaIndex, 'store_ids', storeIndex],
          message: 'Store duplicato nello scope',
        });
      }
      storeIds.add(storeId);
    });
  });
});
export type BrandW3Scope = z.infer<typeof BrandW3ScopeSchema>;

export const BrandW3ManagementContextSchema = z.object({
  user_id: uuidSchema,
  organization_id: uuidSchema,
  role: z.object({
    id: uuidSchema,
    code: codeSchema.max(64),
  }).strict(),
  permissions: z.array(codeSchema.max(128)),
  scope: BrandW3ScopeSchema,
}).strict();
export type BrandW3ManagementContext = z.infer<typeof BrandW3ManagementContextSchema>;

export const BrandW3UserStatusSchema = z.enum([
  'pending_bootstrap',
  'active',
  'locked',
  'deactivated',
]);
export type BrandW3UserStatus = z.infer<typeof BrandW3UserStatusSchema>;

export const BrandW3UserSchema = z.object({
  id: uuidSchema,
  email: emailSchema,
  first_name: nameSchema.max(100),
  last_name: nameSchema.max(100),
  status: BrandW3UserStatusSchema,
  is_local_auth_allowed: z.boolean(),
  totp_enabled: z.boolean(),
  role: BrandW3RoleSchema,
  sector: BrandW3SectorSchema.nullable(),
  identities: z.array(BrandW3IdentitySchema),
  scope: BrandW3ScopeSchema,
  last_login_at: timestampSchema.nullable(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
}).strict();
export type BrandW3User = z.infer<typeof BrandW3UserSchema>;

export const BrandW3SessionSchema = z.object({
  id: uuidSchema,
  user: BrandW3UserSchema,
  identity: BrandW3IdentitySchema,
  permissions: z.array(codeSchema),
  scope: BrandW3ScopeSchema,
  mfa_verified: z.boolean(),
  authenticated_at: timestampSchema,
  expires_at: timestampSchema,
}).strict();
export type BrandW3Session = z.infer<typeof BrandW3SessionSchema>;

export const BrandW3LoginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(1024),
  totpCode: z.string().regex(/^\d{6}$/).optional(),
  backupCode: z.string().min(1).max(64).optional(),
}).strict();
export type BrandW3LoginRequest = z.infer<typeof BrandW3LoginRequestSchema>;

export const BrandW3RefreshRequestSchema = z.object({
  refreshToken: z.string().min(64).max(512),
}).strict();
export type BrandW3RefreshRequest = z.infer<typeof BrandW3RefreshRequestSchema>;

export const BrandW3TokenBundleSchema = z.object({
  token: z.string().min(1),
  refreshToken: z.string().min(64).max(512),
  expiresIn: z.number().int().positive(),
}).strict();
export type BrandW3TokenBundle = z.infer<typeof BrandW3TokenBundleSchema>;

export const BrandW3AuthSuccessResponseSchema = z.object({
  success: z.literal(true),
  data: BrandW3TokenBundleSchema,
}).strict();
export type BrandW3AuthSuccessResponse = z.infer<typeof BrandW3AuthSuccessResponseSchema>;

export const BrandW3TotpEnableRequestSchema = z.object({
  code: z.string().regex(/^\d{6}$/),
}).strict();
export type BrandW3TotpEnableRequest = z.infer<typeof BrandW3TotpEnableRequestSchema>;

export const BrandW3TotpSetupResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    qrCodeDataUri: z.string().min(1),
    manualEntryKey: z.string().min(1),
  }).strict(),
}).strict();
export type BrandW3TotpSetupResponse = z.infer<typeof BrandW3TotpSetupResponseSchema>;

export const BrandW3TotpEnableResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    backupCodes: z.array(z.string().min(1)).min(1),
  }).strict(),
}).strict();
export type BrandW3TotpEnableResponse = z.infer<typeof BrandW3TotpEnableResponseSchema>;

export const BrandW3UserCreateRequestSchema = z.object({
  email: emailSchema,
  first_name: nameSchema.max(100),
  last_name: nameSchema.max(100),
  role_id: uuidSchema,
  // User provisioning in this module is deliberately local + one-time email
  // bootstrap. Federated identity binding remains an explicit, separate flow.
  is_local_auth_allowed: z.literal(true).default(true),
  scope: BrandW3ScopeSchema,
}).strict();
export type BrandW3UserCreateRequest = z.infer<typeof BrandW3UserCreateRequestSchema>;

export const BrandW3UserUpdateRequestSchema = z.object({
  first_name: nameSchema.max(100).optional(),
  last_name: nameSchema.max(100).optional(),
  role_id: uuidSchema.optional(),
  is_local_auth_allowed: z.boolean().optional(),
  scope: BrandW3ScopeSchema.optional(),
}).strict().refine((value) => Object.keys(value).length > 0, {
  message: 'Nessun campo da aggiornare',
});
export type BrandW3UserUpdateRequest = z.infer<typeof BrandW3UserUpdateRequestSchema>;

export const BrandW3SensitiveActionRequestSchema = z.object({
  reason: z.string().trim().min(10).max(500),
}).strict();
export type BrandW3SensitiveActionRequest = z.infer<typeof BrandW3SensitiveActionRequestSchema>;

export const BrandW3AccessBootstrapRequestSchema = z.object({
  token: z.string().min(43).max(512),
  password: z.string()
    .min(12)
    .max(128)
    .regex(/[A-Z]/)
    .regex(/[a-z]/)
    .regex(/[0-9]/)
    .regex(/[^A-Za-z0-9]/),
}).strict();
export type BrandW3AccessBootstrapRequest = z.infer<typeof BrandW3AccessBootstrapRequestSchema>;

export const BrandW3AccessBootstrapResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({ activated: z.literal(true) }).strict(),
}).strict();
export type BrandW3AccessBootstrapResponse = z.infer<typeof BrandW3AccessBootstrapResponseSchema>;

export const BrandW3AccessResetResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    reset: z.literal(true),
    delivery: z.enum(['email', 'idp']),
  }).strict(),
}).strict();
export type BrandW3AccessResetResponse = z.infer<typeof BrandW3AccessResetResponseSchema>;

export const BrandW3RoleWriteRequestSchema = z.object({
  code: codeSchema.max(64),
  name: nameSchema,
  description: z.string().trim().max(1000).nullable().optional(),
  sector_id: uuidSchema,
  permission_ids: z.array(uuidSchema).max(1000).default([]),
}).strict();
export type BrandW3RoleWriteRequest = z.infer<typeof BrandW3RoleWriteRequestSchema>;

export const BrandW3SectorWriteRequestSchema = z.object({
  code: codeSchema.max(64),
  name: nameSchema,
}).strict();
export type BrandW3SectorWriteRequest = z.infer<typeof BrandW3SectorWriteRequestSchema>;

export const BrandW3ListQuerySchema = z.object({
  search: z.string().trim().max(255).optional(),
  include_inactive: z.coerce.boolean().default(false),
  status: BrandW3UserStatusSchema.optional(),
  role_id: uuidSchema.optional(),
  sector_id: uuidSchema.optional(),
  cursor: z.string().min(1).max(512).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
}).strict();
export type BrandW3ListQuery = z.infer<typeof BrandW3ListQuerySchema>;

export const BrandW3IdParamsSchema = z.object({ id: uuidSchema }).strict();

export const BrandW3AddressSchema = z.object({
  address: z.string().max(500).nullable(),
  city: z.string().max(100).nullable(),
  postal_code: z.string().max(20).nullable(),
  country: z.string().max(100).nullable(),
}).strict();
export type BrandW3Address = z.infer<typeof BrandW3AddressSchema>;

export const BrandW3AreaSchema = z.object({
  id: uuidSchema,
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(128),
  brand: z.literal('W3'),
  is_active: z.boolean(),
}).strict();
export type BrandW3Area = z.infer<typeof BrandW3AreaSchema>;

export const BrandW3StoreLookupQuerySchema = z.object({
  search: z.string().trim().max(255).optional(),
  area_id: uuidSchema.optional(),
  cursor: z.string().min(1).max(512).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
}).strict();
export type BrandW3StoreLookupQuery = z.infer<typeof BrandW3StoreLookupQuerySchema>;

export const BrandW3StoreLookupItemSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  product: BrandW3ProductSchema,
  area_id: uuidSchema,
  area_name: z.string().min(1).max(255),
  name: z.string().min(1).max(255),
  code: z.string().max(100).nullable(),
  retail_code: z.string().max(64).nullable().default(null),
  address: BrandW3AddressSchema.default({
    address: null,
    city: null,
    postal_code: null,
    country: null,
  }),
  brands: z.array(z.enum(['WINDTRE', 'VERY'])).min(1),
  channels: z.array(z.object({
    id: uuidSchema,
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(128),
  }).strict()).default([]),
}).strict();
export type BrandW3StoreLookupItem = z.infer<typeof BrandW3StoreLookupItemSchema>;

export const BrandW3ErrorSchema = z.object({
  code: z.string().min(1).max(128),
  message: z.string().min(1).max(1000),
  request_id: uuidSchema,
  details: z.record(z.string(), z.unknown()).optional(),
}).strict();
export type BrandW3Error = z.infer<typeof BrandW3ErrorSchema>;

export const BrandW3ErrorResponseSchema = z.object({
  success: z.literal(false),
  error: BrandW3ErrorSchema,
  requires2FA: z.boolean().optional(),
  requiresTOTPSetup: z.boolean().optional(),
}).strict();
export type BrandW3ErrorResponse = z.infer<typeof BrandW3ErrorResponseSchema>;

export const BrandW3PaginationSchema = z.object({
  limit: z.number().int().min(1).max(200),
  next_cursor: z.string().min(1).max(512).nullable(),
  has_more: z.boolean(),
}).strict();
export type BrandW3Pagination = z.infer<typeof BrandW3PaginationSchema>;

export const BrandW3UserPageSchema = z.object({
  items: z.array(BrandW3UserSchema),
  pagination: BrandW3PaginationSchema,
}).strict();
export type BrandW3UserPage = z.infer<typeof BrandW3UserPageSchema>;

export const BrandW3SectorPageSchema = z.object({
  items: z.array(BrandW3SectorSchema),
  pagination: BrandW3PaginationSchema,
}).strict();

export const BrandW3RolePageSchema = z.object({
  items: z.array(BrandW3RoleSchema),
  pagination: BrandW3PaginationSchema,
}).strict();

export const BrandW3PermissionPageSchema = z.object({
  items: z.array(BrandW3PermissionSchema),
  pagination: BrandW3PaginationSchema,
}).strict();

export const BrandW3AreaPageSchema = z.object({
  items: z.array(BrandW3AreaSchema),
  pagination: BrandW3PaginationSchema,
}).strict();

export const BrandW3StoreLookupPageSchema = z.object({
  items: z.array(BrandW3StoreLookupItemSchema),
  pagination: BrandW3PaginationSchema,
}).strict();
export type BrandW3StoreLookupPage = z.infer<typeof BrandW3StoreLookupPageSchema>;